import React, { useState, useEffect } from 'react';
import { LuCircleCheck, LuCircleX, LuClock, LuReceipt, LuRefreshCw, LuTrash2, LuWallet } from 'react-icons/lu';
import { db, auth } from '../config/firebaseConfig';
import { collection, getDocs, query, where, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { useUserProfile } from '../contexts/UserProfileContext';
import { useTranslation } from '../i18n/LanguageContext';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import FilterTiles from './data/FilterTiles';
import { CARD, EmptyState, HelpNote, LoadingRows, SearchField } from '../pages/ProfilePage/components/ProfileUi';
import { expenseCategoryLabel } from '../pages/ProfilePage/expense/expenseOptions';
import ExpenseRequestCard from './expenseRequests/ExpenseRequestCard';
import { ReasonDialog, ReasonFormDialog } from './expenseRequests/ReasonDialogs';

const ExpenseRequests = () => {
  const [expenseRequests, setExpenseRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [showEditReasonModal, setShowEditReasonModal] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editReasonText, setEditReasonText] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingExpenseId, setRejectingExpenseId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const { employeeId, userAuthorization } = useUserProfile();
  const { t, language, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.expense.approvals.${key}`, vars);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Admin kontrolü
  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const usersRef = collection(db, 'Users');
          const [userDocSnap, emailSnapshot, eMailSnapshot] = await Promise.all([
            getDocs(query(usersRef, where('uid', '==', user.uid))),
            getDocs(query(usersRef, where('email', '==', user.email))),
            getDocs(query(usersRef, where('eMail', '==', user.email)))
          ]);

          if (
            (!userDocSnap.empty && userDocSnap.docs[0].data().role === 'admin') ||
            (!emailSnapshot.empty && emailSnapshot.docs[0].data().role === 'admin') ||
            (!eMailSnapshot.empty && eMailSnapshot.docs[0].data().role === 'admin') ||
            (userAuthorization && userAuthorization.includes('admin'))
          ) {
            setIsUserAdmin(true);
          }
        } catch (error) {
          console.error("Error checking admin status:", error);
        }
      }
    };

    checkAdminStatus();
  }, [userAuthorization]);

  useEffect(() => {
    if (isUserAdmin || employeeId) {
      fetchExpenseRequests(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, isUserAdmin]);

  const fetchExpenseRequests = async (showLoading = false) => {
    if (!employeeId && !isUserAdmin) {
      console.log('Employee ID not available yet:', employeeId);
      return;
    }
    
    try {
      if (showLoading || expenseRequests.length === 0) {
        setIsLoading(true);
      }
      const expenseRequestsRef = collection(db, 'ExpenseRequests');
      
      let querySnapshot;
      if (isUserAdmin) {
        // Super admin tüm talepleri görebilir
        querySnapshot = await getDocs(expenseRequestsRef);
        console.log('Fetching all expense requests for admin');
      } else {
        // Normal kullanıcı sadece kendi taleplerini görebilir
        const q = query(expenseRequestsRef, where('employeeId', '==', employeeId));
        querySnapshot = await getDocs(q);
        console.log('Fetching expense requests for employeeId:', employeeId);
      }
      
      const requests = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Fetched expense requests:', requests);
      
      // Sort by creation date (newest first)
      requests.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      
      setExpenseRequests(requests);
    } catch (error) {
      console.error('Error fetching expense requests:', error);
      toast.error(label('toast.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    try {
      await deleteDoc(doc(db, 'ExpenseRequests', requestId));
      toast.success(label('toast.deleted'));
      fetchExpenseRequests();
    } catch (error) {
      console.error('Error deleting expense request:', error);
      toast.error(label('toast.deleteError', { message: error.message }));
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    if (!isUserAdmin) {
      toast.error(label('toast.noPermission'));
      return;
    }

    // If rejecting, show modal for reason
    if (newStatus === 'rejected') {
      setRejectingExpenseId(requestId);
      setShowRejectModal(true);
      return;
    }

    // For approval, proceed directly
    try {
      await updateDoc(doc(db, 'ExpenseRequests', requestId), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
        processedBy: auth.currentUser?.email || 'Admin'
      });
      
      // Update local state
      setExpenseRequests(prev => prev.map(req => 
        req.id === requestId 
          ? { ...req, status: newStatus, updatedAt: new Date().toISOString() }
          : req
      ));
      
      toast.success(label('toast.approved'));
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error(label('toast.statusError', { message: error.message }));
    }
  };

  const handleRejectWithReason = async () => {
    if (!rejectionReason.trim()) {
      toast.error(label('toast.reasonRequired'));
      return;
    }

    if (!isUserAdmin) {
      toast.error(label('toast.noPermission'));
      return;
    }

    try {
      await updateDoc(doc(db, 'ExpenseRequests', rejectingExpenseId), {
        status: 'rejected',
        rejectionReason: rejectionReason.trim(),
        rejectedAt: new Date().toISOString(),
        rejectedBy: auth.currentUser?.email || 'Admin',
        updatedAt: new Date().toISOString(),
        processedBy: auth.currentUser?.email || 'Admin'
      });

      // Update local state
      setExpenseRequests(prev => prev.map(req => 
        req.id === rejectingExpenseId 
          ? { 
              ...req, 
              status: 'rejected',
              rejectionReason: rejectionReason.trim(),
              rejectedAt: new Date().toISOString(),
              rejectedBy: auth.currentUser?.email || 'Admin',
              updatedAt: new Date().toISOString()
            }
          : req
      ));
      
      toast.success(label('toast.rejected'));
      setShowRejectModal(false);
      setRejectionReason('');
      setRejectingExpenseId(null);
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(label('toast.statusError', { message: error.message }));
    }
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
    setRejectionReason('');
    setRejectingExpenseId(null);
  };

  const handlePdfView = (pdfUrl, fileName) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else {
      toast.error(label('toast.pdfMissing'));
    }
  };

  const handlePdfDownload = (pdfUrl, fileName) => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName || 'masraf-belgesi.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast.error(label('toast.pdfMissing'));
    }
  };

  const handleViewReason = (reason) => {
    setSelectedReason(reason);
    setShowReasonModal(true);
  };

  const handleEditReasonClick = (expenseId, currentReason) => {
    setEditingExpenseId(expenseId);
    setEditReasonText(currentReason || '');
    setShowEditReasonModal(true);
  };

  const handleUpdateReason = async () => {
    if (!editReasonText.trim()) {
      toast.warn(label('toast.reasonRequired'));
      return;
    }

    if (!isUserAdmin) {
      toast.error(label('toast.noPermission'));
      return;
    }

    try {
      await updateDoc(doc(db, 'ExpenseRequests', editingExpenseId), {
        rejectionReason: editReasonText.trim(),
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setExpenseRequests(prev => prev.map(req => 
        req.id === editingExpenseId 
          ? { ...req, rejectionReason: editReasonText.trim() }
          : req
      ));

      toast.success(label('toast.reasonUpdated'));
      setShowEditReasonModal(false);
      setEditingExpenseId(null);
      setEditReasonText('');
    } catch (error) {
      console.error('Error updating rejection reason:', error);
      toast.error(label('toast.updateError', { message: error.message }));
    }
  };

  // Silme onayı ConfirmDialog ile alınır
  const confirmDelete = async () => {
    setDeleting(true);
    await handleDeleteRequest(deleteId);
    setDeleting(false);
    setDeleteId(null);
  };

  const statusKey = (request) => (request.status === 'approved' || request.status === 'rejected' ? request.status : 'pending');
  const countOf = (status) => expenseRequests.filter((request) => statusKey(request) === status).length;
  const pendingAmount = expenseRequests
    .filter((request) => statusKey(request) === 'pending')
    .reduce((sum, request) => sum + (Number(request.amount) || 0), 0);

  const needle = search.trim().toLocaleLowerCase('tr-TR');
  const visible = expenseRequests.filter((request) =>
    (filter === 'all' || statusKey(request) === filter) &&
    (!needle || [request.employeeName, request.category, expenseCategoryLabel(t, request.category), request.description]
      .some((value) => String(value || '').toLocaleLowerCase('tr-TR').includes(needle)))
  );
  const filtered = filter !== 'all' || Boolean(needle);

  const tiles = [
    { key: 'all', value: expenseRequests.length, icon: LuReceipt, tone: 'neutral' },
    { key: 'pending', value: countOf('pending'), icon: LuClock, tone: 'warning', highlight: countOf('pending') > 0 },
    { key: 'approved', value: countOf('approved'), icon: LuCircleCheck, tone: 'success' },
    { key: 'rejected', value: countOf('rejected'), icon: LuCircleX, tone: 'danger' },
  ].map((tile) => ({ ...tile, label: label(`stats.${tile.key}`) }));
  const pendingTotal = `₺${pendingAmount.toLocaleString(locale)}`;
  tiles.push({
    key: 'pendingAmount',
    static: true,
    compact: true,
    value: pendingTotal,
    title: pendingTotal,
    icon: LuWallet,
    tone: 'violet',
    label: label('stats.pendingAmount'),
    shortLabel: label('stats.pendingAmountShort'),
    className: 'tw-col-span-2 sm:tw-col-span-1',
  });

  let body;
  if (isLoading) {
    body = <LoadingRows count={4} height="tw-h-40" label={label('loading')} />;
  } else if (expenseRequests.length === 0) {
    body = <EmptyState icon={LuReceipt} title={label('empty')} text={isUserAdmin ? label('emptyHint') : label('emptyOwnHint')} />;
  } else if (visible.length === 0) {
    body = (
      <EmptyState
        compact
        icon={LuReceipt}
        title={label('noMatch')}
        text={label('noMatchHint')}
        action={(
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setFilter('all');
              setSearch('');
            }}
          >
            {label('clearFilters')}
          </Button>
        )}
      />
    );
  } else {
    body = (
      <ul className="tw-grid tw-gap-3 md:tw-grid-cols-2">
        {visible.map((request) => (
          <ExpenseRequestCard
            key={request.id}
            request={request}
            isUserAdmin={isUserAdmin}
            onApprove={() => handleStatusUpdate(request.id, 'approved')}
            onReject={() => handleStatusUpdate(request.id, 'rejected')}
            onDelete={() => setDeleteId(request.id)}
            onViewReason={handleViewReason}
            onEditReason={handleEditReasonClick}
            onPdfView={handlePdfView}
            onPdfDownload={handlePdfDownload}
          />
        ))}
      </ul>
    );
  }

  return (
    <div lang={language} className="tw-space-y-4">
      <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center">
        <HelpNote className="lg:tw-flex-1">{isUserAdmin ? label('help') : label('helpOwn')}</HelpNote>
        <Button onClick={() => fetchExpenseRequests(true)} disabled={isLoading} className="max-sm:tw-w-full sm:tw-self-start lg:tw-self-auto">
          <LuRefreshCw aria-hidden="true" className={`tw-h-4 tw-w-4 ${isLoading ? 'tw-animate-spin' : ''}`} />
          {label('refresh')}
        </Button>
      </div>

      {!isLoading && expenseRequests.length > 0 && (
        <FilterTiles
          tiles={tiles}
          value={filter}
          onChange={setFilter}
          label={label('stats.label')}
          className="tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-5"
        />
      )}

      <div className={`${CARD} tw-space-y-3 tw-p-3 sm:tw-p-4`}>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-2">
          <h3 className="tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
            {isUserAdmin ? label('titleAll') : label('titleOwn')}
            <span className="tw-ml-2 tw-text-xs tw-font-medium tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">
              {filtered ? `${visible.length} / ${expenseRequests.length}` : label('count', { count: expenseRequests.length })}
            </span>
          </h3>
          {!isLoading && expenseRequests.length > 0 && (
            <SearchField value={search} onChange={setSearch} placeholder={label('search')} className="tw-w-full sm:tw-w-72" />
          )}
        </div>
        {body}
      </div>

      <ReasonDialog isOpen={showReasonModal} reason={selectedReason} onClose={() => setShowReasonModal(false)} />
      <ReasonFormDialog
        isOpen={showEditReasonModal}
        mode="edit"
        value={editReasonText}
        onChange={setEditReasonText}
        onCancel={() => setShowEditReasonModal(false)}
        onSubmit={handleUpdateReason}
      />
      <ReasonFormDialog
        isOpen={showRejectModal}
        mode="reject"
        value={rejectionReason}
        onChange={setRejectionReason}
        onCancel={handleCancelReject}
        onSubmit={handleRejectWithReason}
      />
      <ConfirmDialog
        isOpen={Boolean(deleteId)}
        tone="danger"
        icon={LuTrash2}
        title={label('deleteDialog.title')}
        message={label('deleteDialog.message')}
        confirmLabel={label('deleteDialog.confirm')}
        cancelLabel={label('deleteDialog.cancel')}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
};

export default ExpenseRequests;
